/* import __COLOCATED_TEMPLATE__ from './index.hbs'; */
import { action, computed } from '@ember/object';
import { inject as service } from '@ember/service';
import { lt } from '@ember/object/computed';
import Component from '@glimmer/component';
import { assert } from '@ember/debug';
import { isNone } from '@ember/utils';
import GroupCondition from 'cio/app-models/conditions/group';
import { any, all } from 'cio/app-models/conditions/group';
import { forEachSegmentable } from 'cio/utils/action-info';
import { createCondition } from 'cio/utils/conditions';
import { guidFor } from '@ember/object/internals';
import FlyDragDropManager from 'fly-drag-drop/services/fly-drag-drop-manager';
import EnvironmentService from 'cio/services/environment';
import { MainConditionInterface } from 'cio/app-models/conditions/abstract';
import { tracked } from '@glimmer/tracking';
import { ComponentLike } from '@glint/template';
import AuthService from 'cio/services/auth';
import FiltersService from 'cio/services/filters';
import SegmentModel from 'cio/models/segment';
import AttributeCondition from 'cio/app-models/conditions/attribute';
import ObjectTypesService from 'cio/services/object-types';
import { duplicateableConditions } from 'cio/utils/condition-editor';
import AttributeComponent from 'cio/components/cio-condition-editor-attribute';
import DeviceAttributeComponent from 'cio/components/cio-condition-editor-device-attribute';
import EmailComponent from 'cio/components/cio-condition-editor-email';
import EventComponent from 'cio/components/cio-condition-editor-event';
import RelationshipComponent from 'cio/components/cio-condition-editor-relationship';
import FormComponent from 'cio/components/cio-condition-editor-form';
import PageviewComponent from 'cio/components/cio-condition-editor-pageview';
import ScreenviewComponent from 'cio/components/cio-condition-editor-screenview';
import ActionComponent from 'cio/components/cio-condition-editor-action';
import UnknownComponent from 'cio/components/cio-condition-editor-unknown';
import ActionCondition from 'cio/app-models/conditions/action';

interface CioConditionEditorGroupArgs {
  condition: GroupCondition;
  allowMessageWildcardSelection: boolean;
  allowInParentWildcardSelection: boolean;
  draggingEnabled?: boolean;
  groupItemsShort?: ITEM[];
  groupItems?: ITEM[];
  actionItems?: ITEM[];
  MAX_DEPTH?: number;
  depth?: number;
  classNames?: string;
  segment?: SegmentModel;
  disabled: boolean;
}

const MAX_DEPTH = 2;
type ITEM = {
  value: string;
  label: string;
};

const GROUP_ITEMS: ITEM[] = [
  { value: all, label: 'All (AND)' },
  { value: any, label: 'At least one (OR)' },
];
const GROUP_ITEMS_SHORT: ITEM[] = [
  { value: all, label: 'All' },
  { value: any, label: 'At least one' },
];

const RELATIONSHIP_CUSTOMER_ITEMS = [{ value: 'relationship', label: 'Relationship' }];

const CUSTOMER_ITEMS: ITEM[] = [
  { value: 'attribute', label: 'Attribute' },
  { value: 'event', label: 'Event' },
  { value: 'form', label: 'Form' },
  { value: 'pageview', label: 'Page' },
  { value: 'device_attribute', label: 'Device' },
  { value: 'screenview', label: 'Screen' },
];

export default class CioConditionEditorGroup extends Component<CioConditionEditorGroupArgs> {
  @service auth!: AuthService;
  @service flyDragDropManager!: FlyDragDropManager;
  @service environment!: EnvironmentService;
  @service filters!: FiltersService;
  @service objectTypes!: ObjectTypesService;

  readonly groupName = guidFor(this);
  private components: Record<string, ComponentLike<any>> = {
    group: CioConditionEditorGroup,
    attribute: AttributeComponent,
    device_attribute: DeviceAttributeComponent,
    email: EmailComponent,
    event: EventComponent,
    relationship: RelationshipComponent,
    form: FormComponent,
    pageview: PageviewComponent,
    screenview: ScreenviewComponent,
  };

  get customerItems() {
    let items = [...CUSTOMER_ITEMS];

    if (this.objectTypes.enabledObjectTypesCount > 0) {
      items.splice(2, 0, ...RELATIONSHIP_CUSTOMER_ITEMS);
    }

    return items;
  }

  @tracked actionItems: ITEM[] | null = null;
  @lt('depth', MAX_DEPTH) canAddGroup!: boolean;

  constructor(owner: unknown, args: CioConditionEditorGroupArgs) {
    super(owner, args);
    this.setActionItems();

    forEachSegmentable((action: string) => {
      this.components[action] = ActionComponent;
    });
  }

  @action
  private setActionItems() {
    let { environment } = this;
    let actionItems = [{ value: 'email', label: 'Email', icon: 'email' }].slice();

    forEachSegmentable(function (action: string, info) {
      if (environment.enabledAction(action)) {
        actionItems.push({
          label: info.name,
          value: action,
          icon: info.icon,
        });
      }
    });

    this.actionItems = actionItems;
  }

  @computed('args.condition.operator')
  get operatorClassName() {
    let { operator } = this.args.condition;
    return `cio-condition-editor-group--${operator}`;
  }

  @computed('depth')
  get depthClassModifier() {
    return `${this.depth % 2 === 0 ? 'even' : 'odd'}`;
  }

  @computed('depth')
  get handleClassName() {
    if (this.depth === 0) {
      return null;
    }
    return `cio-condition-editor-group--has-handle`;
  }

  get draggingEnabled() {
    return this.args.draggingEnabled ?? true;
  }

  get groupItemsShort() {
    return this.args.groupItemsShort ?? GROUP_ITEMS_SHORT;
  }
  get groupItems() {
    return this.args.groupItems ?? GROUP_ITEMS;
  }

  get MAX_DEPTH() {
    return this.args.MAX_DEPTH ?? MAX_DEPTH;
  }

  get depth() {
    return this.args.depth ?? 0;
  }

  get duplicateableConditions() {
    return duplicateableConditions;
  }

  @computed('args.condition.children.@each.{action,email}')
  get children() {
    let conditions = this.args.condition.children;

    if (isNone(conditions)) {
      return;
    }

    return conditions.map((condition) => {
      // @ts-ignore - TS doesn't pick up all the properties
      let { type, action, email }: { type: string; action: string; email: string } = condition;

      let isBranchMessage = this.filters.isBranchMessage(action ?? email);
      if (condition != null && isBranchMessage && type === 'output') {
        type = this.filters.branchMessageTypes[action ?? email];
        condition.type = type;
      }

      let component = this.segmentEditComponent(condition);

      return { component, condition, isBranchMessage };
    });
  }

  @computed('args.condition.children.[]')
  get childrenIsEmpty() {
    return (this.args.condition.children || []).length === 0;
  }

  get objectTypesIds() {
    return this.objectTypes.enabledObjectTypes.map((objectType) => objectType.id);
  }

  @action
  addCondition(value: string) {
    let conditions = this.args.condition.children;
    let condition = this.createCondition(value);

    conditions.pushObject(condition);

    return false;
  }

  private createCondition(value: string) {
    let condition;

    if (value === any || value === all) {
      condition = GroupCondition.create({ operator: value });
    } else {
      let properties: Record<string, any> = {
        defaultToWildcard: this.args.allowMessageWildcardSelection,
        shouldValidate: true,
      };
      if (value === RELATIONSHIP_CUSTOMER_ITEMS[0].value && this.objectTypesIds[0] != null) {
        properties.typeId = this.objectTypesIds[0];
      }
      condition = createCondition(value, properties);
    }

    return condition;
  }

  private getConditionIndex(condition: MainConditionInterface) {
    let conditions = this.args.condition.children;
    let index = conditions.indexOf(condition);

    assert(`Tried to get index for "${condition}" but was not found`, index >= 0);
    return index;
  }

  private segmentEditComponent(condition: MainConditionInterface | ActionCondition | null) {
    if (condition == null) {
      return UnknownComponent;
    }

    return this.components[(condition as ActionCondition).uiType ?? condition.type] ?? UnknownComponent;
  }

  @action
  removeCondition(condition: MainConditionInterface) {
    let conditions = this.args.condition.children;
    let index = this.getConditionIndex(condition);
    if (index < 0) {
      return;
    }

    if (condition.type === 'group') {
      //@ts-ignore `children` is not seen to exist but it does
      let { children } = condition;
      children?.forEach((child: MainConditionInterface, i: number) => conditions.insertAt(index + i, child));
    }

    conditions.removeObject(condition);
  }

  @action
  duplicateCondition(condition: AttributeCondition) {
    let duplicateCondition = condition.duplicate();
    assert(`Duplication of condition type: "${condition.type}" is not supported`, duplicateCondition != null);
    let conditions = this.args.condition.children;
    let index = this.getConditionIndex(condition);
    conditions.insertAt(index + 1, duplicateCondition);
  }

  @action
  replaceCondition(condition: MainConditionInterface, type: string) {
    let conditions = this.args.condition.children;
    let index = conditions.indexOf(condition);
    assert(`Tried to replace "${condition}" but was not found`, index >= 0);
    let properties: Record<string, any> = {};

    if (type === RELATIONSHIP_CUSTOMER_ITEMS[0].value && this.objectTypesIds[0] != null) {
      properties.typeId = this.objectTypesIds[0];
    }

    conditions.replace(index, 1, [createCondition(type, properties)]);
  }

  @action
  //@ts-ignore @todo: add types expected here..
  dragSortEnded({ sourceList, sourceIndex, targetList, targetIndex, item }) {
    if (targetIndex === null || (sourceList === targetList && sourceIndex === targetIndex)) {
      return;
    }

    sourceList.removeAt(sourceIndex);
    targetList.insertAt(targetIndex, item);
  }
}
